import type { GenerationMetadata, ProcessingStatus, SubmissionPayload } from '@/shared/types';
import {
  createPaperVideo,
  paperFromUrl,
  uploadPaper,
} from '@/features/generation/services/papersApi';
import { requestVideoGeneration } from '@/features/generation/services/videoGenerationApi';
import { UserFacingError } from '@/shared/utils/errors';

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB; PDFs/.docx routinely exceed 1.5MB

export function getMaxFileSizeBytes() {
  return MAX_FILE_SIZE_BYTES;
}

export type SubmissionResponse = {
  jobId: string;
  etaSeconds: number;
  videoId: string;
  status: ProcessingStatus;
  aspectRatio: string;
  durationSeconds: number;
  metadata: GenerationMetadata;
};

export function newVideoSubmitIdempotencyKey(): string {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi?.randomUUID) {
    return cryptoApi.randomUUID();
  }
  if (cryptoApi?.getRandomValues) {
    const bytes = new Uint8Array(16);
    cryptoApi.getRandomValues(bytes);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  }
  throw new UserFacingError('Secure browser randomness is unavailable. Refresh and try again.');
}

export async function submitToValoiEngine(
  payload: SubmissionPayload,
  userId: string,
  idempotencyKey: string,
): Promise<SubmissionResponse> {
  if (payload.arxivId || payload.paperFile || payload.paperUrl) {
    return submitPaper(payload);
  }

  if (!payload.file && !payload.text?.trim()) {
    throw new UserFacingError('Submission requires text content or a file.');
  }

  if (userId && !payload.userId) {
    payload.userId = userId;
  }

  const generation = await requestVideoGeneration(payload, { idempotencyKey });
  return {
    jobId: generation.jobId,
    etaSeconds: generation.etaSeconds,
    videoId: generation.jobId,
    status: generation.status,
    aspectRatio: generation.aspectRatio,
    durationSeconds: generation.requestedDurationSeconds,
    metadata: generation.metadata,
  };
}

/*
 * Paper submissions take the papers route: the backend reads the arXiv PDF and
 * composes the script itself, so nothing here sends text, a file, or a style —
 * the returned job then flows through the ordinary confirm/progress path.
 *
 * The metadata mirrors what the status poll will report back (documentation
 * style, the Wan renderer) so the queue card reads correctly before the first
 * poll lands, rather than showing a blank badge for a second.
 */
async function submitPaper(payload: SubmissionPayload): Promise<SubmissionResponse> {
  const result = payload.paperFile
    ? await uploadPaper(payload.paperFile)
    : payload.paperUrl
      ? await paperFromUrl(payload.paperUrl)
      : await createPaperVideo(payload.arxivId ?? '');
  return {
    jobId: result.jobId,
    /* How long until the video is READY. Papers render as multi-minute
       documentation videos, and a generous estimate beats a countdown that hits
       zero while the plan is still being written. */
    etaSeconds: 300,
    videoId: result.jobId,
    status: result.status,
    /* Everything below mirrors what /papers/video asks the engine for, so the
       queue card reads correctly before the first status poll lands. These are
       the engine's OWN defaults (app/datatypes.py::GenerateRequest), not values
       chosen here: papers_api builds a GenerateRequest with only text/title/
       author/style set and lets the rest default. If a default moves, this drifts
       — the poll corrects it a second later, but the card lies until then. */
    aspectRatio: '16:9',
    // Seconds PER SCENE, not the video's runtime. GenerateRequest.duration
    // defaults to 10; this said 6, which no path ever produces.
    durationSeconds: 10,
    metadata: {
      modelName: 'wan-i2v',
      provider: 'wan',
      style: 'documentation',
      aspectRatio: '16:9',
    },
  };
}
